import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { ColorPicker } from "./ColorPicker";

export type Stage = {
  id: number;
  name: string;
  position: number;
  color: string;
  probability: number;
  isWon: boolean;
  isLost: boolean;
};

export function SortableStage({
  stage,
  onChange,
  onDelete,
}: {
  stage: Stage;
  onChange: (patch: Partial<Stage>) => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const terminalBadge = stage.isWon ? (
    <Badge className="bg-emerald-600 text-[10px] h-5">
      <CheckCircle2 className="h-3 w-3 mr-1" />
      Fechado
    </Badge>
  ) : stage.isLost ? (
    <Badge className="bg-red-600 text-[10px] h-5">
      <XCircle className="h-3 w-3 mr-1" />
      Perdido
    </Badge>
  ) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex flex-wrap items-center gap-2 bg-background border rounded-lg px-2 py-2.5"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground shrink-0"
        title="Arrastar"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <ColorPicker value={stage.color} onChange={(c) => onChange({ color: c })} />
      <Input
        defaultValue={stage.name}
        onBlur={(e) => {
          if (e.target.value !== stage.name && e.target.value.trim()) {
            onChange({ name: e.target.value.trim() });
          }
        }}
        className="h-8 flex-1 min-w-[120px]"
      />
      {!stage.isWon && !stage.isLost && (
        <div className="flex items-center gap-1.5 w-full sm:w-[160px] order-last sm:order-none">
          <Slider
            value={[stage.probability]}
            min={0}
            max={100}
            step={5}
            onValueChange={(v) => onChange({ probability: v[0] })}
            className="flex-1"
          />
          <span className="text-xs tabular-nums w-9 text-right">
            {stage.probability}%
          </span>
        </div>
      )}
      {terminalBadge}
      <Button
        size="icon"
        variant="ghost"
        className="sm:opacity-0 sm:group-hover:opacity-100 shrink-0 h-8 w-8"
        onClick={onDelete}
        title="Excluir estágio"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
